// useSimplePeriodDropdown.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

export type Range = { from: number | null; to: number | null };

// Predefined keys you can pass to the hook or to changePeriodDropdown()
export type PeriodKey =
  | 'today'
  | 'thisMonth'
  | 'thisQuarter'
  | 'thisHalf'
  | 'thisFinancialYear'
  | 'custom';

type Options = {
  /** When true (default), calculations stay inside Redux FY (financialYear.startdate/enddate). */
  useFYFromRedux?: boolean;
  /**
   * If true (default), “month math” treats calendar months (Jan..Dec).
   * If false, months are computed in fiscal order from FY start.
   */
  monthIdsAreCalendar?: boolean;
  handleChange?: (r: Range) => void;
};

const toUnix = (d: Date) => Math.floor(d.getTime() / 1000);
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay   = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const dayRangeForTs = (tsSec: number) => {
  const d = new Date(tsSec * 1000);
  return { from: toUnix(startOfDay(d)), to: toUnix(endOfDay(d)) };
};
const normSec = (x: number | null) => (typeof x === 'number' ? (x > 1e12 ? Math.floor(x / 1000) : x) : null);
const normalizeRange = (r: Range): Range => ({ from: normSec(r.from), to: normSec(r.to) });

export function useSimplePeriodDropdown(
  defaultKey: PeriodKey = 'custom',
  opts: Options = {},
  handleChange?: (r: Range) => void
) {
  const { useFYFromRedux = true, monthIdsAreCalendar = true } = opts;

  // FY from Redux (numbers; typed any per your constraint)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const financialYear: any = useSelector((state: any) => state?.userPrefrenceandReducer?.financialYear);
  const rawFyStart: number | null = typeof financialYear?.startdate === 'number' ? financialYear.startdate : null;
  const rawFyEnd: number | null   = typeof financialYear?.enddate   === 'number' ? financialYear.enddate   : null;

  const fyStart = useMemo(() => normSec(rawFyStart), [rawFyStart]);
  const fyEnd   = useMemo(() => normSec(rawFyEnd),   [rawFyEnd]);
  const fyReady = typeof fyStart === 'number' && typeof fyEnd === 'number';

  const clampToFY = useCallback((from: number, to: number): Range => ({
    from: Math.max(from, fyStart as number),
    to:   Math.min(to,   fyEnd   as number),
  }), [fyStart, fyEnd]);

  // month/quarter/half helpers in FY-space
  const rangeForFYWindow = useCallback((startMonthOffset: number, monthsLen: number): Range => {
    const base = new Date((fyStart as number) * 1000);
    const baseYear = base.getFullYear();
    const baseMonth = base.getMonth(); // 0..11
    const targetMonthAbs = baseMonth + startMonthOffset;
    const targetYear = baseYear + Math.floor(targetMonthAbs / 12);
    const targetMonth = ((targetMonthAbs % 12) + 12) % 12;
    const start = new Date(targetYear, targetMonth, 1, 0, 0, 0, 0);
    const end   = new Date(targetYear, targetMonth + monthsLen, 0, 23, 59, 59, 999);
    return clampToFY(toUnix(start), toUnix(end));
  }, [fyStart, clampToFY]);

  const calendarIdToFyOffset = useCallback((calendarId: number) => {
    const fyStartMonth = new Date((fyStart as number) * 1000).getMonth(); // 0..11
    const calMonth = calendarId - 1; // 0..11
    return ((calMonth - fyStartMonth) + 12) % 12; // 0..11
  }, [fyStart]);

  const computeThisMonthFY = useCallback((): Range | null => {
    if (!fyReady) return null;
    const now = new Date();
    const nowSec = Math.floor(now.getTime() / 1000);
    if (nowSec < (fyStart as number) || nowSec > (fyEnd as number)) return null; // outside FY

    if (monthIdsAreCalendar) {
      const monthId = now.getMonth() + 1; // 1..12
      const offset = calendarIdToFyOffset(monthId);
      return rangeForFYWindow(offset, 1);
    } else {
      // fiscal-order: offset from FY start
      const offset = ((now.getMonth() - new Date((fyStart as number) * 1000).getMonth()) + 12) % 12;
      return rangeForFYWindow(offset, 1);
    }
  }, [fyReady, fyStart, fyEnd, monthIdsAreCalendar, calendarIdToFyOffset, rangeForFYWindow]);

  const computeThisQuarterFY = useCallback((): Range | null => {
    if (!fyReady) return null;
    const now = new Date();
    const nowSec = Math.floor(now.getTime() / 1000);
    if (nowSec < (fyStart as number) || nowSec > (fyEnd as number)) return null;
    const baseMonth = new Date((fyStart as number) * 1000).getMonth();
    const offset = ((now.getMonth() - baseMonth) + 12) % 12; // 0..11
    const qStart = Math.floor(offset / 3) * 3; // 0,3,6,9
    return rangeForFYWindow(qStart, 3);
  }, [fyReady, fyStart, fyEnd, rangeForFYWindow]);

  const computeThisHalfFY = useCallback((): Range | null => {
    if (!fyReady) return null;
    const now = new Date();
    const nowSec = Math.floor(now.getTime() / 1000);
    if (nowSec < (fyStart as number) || nowSec > (fyEnd as number)) return null;
    const baseMonth = new Date((fyStart as number) * 1000).getMonth();
    const offset = ((now.getMonth() - baseMonth) + 12) % 12; // 0..11
    const hStart = Math.floor(offset / 6) * 6; // 0 or 6
    return rangeForFYWindow(hStart, 6);
  }, [fyReady, fyStart, fyEnd, rangeForFYWindow]);

  // compute by key (FY-aware when enabled)
  const computeByKey = useCallback((key: PeriodKey): Range | null => {
    if (key === 'custom') return { from: null, to: null };

    if (useFYFromRedux && fyReady) {
      const nowSec = Math.floor(Date.now() / 1000);

      switch (key) {
        case 'today': {
          if (nowSec < (fyStart as number) || nowSec > (fyEnd as number)) return null;
          const r = dayRangeForTs(nowSec);
          return clampToFY(r.from, r.to);
        }
        case 'thisFinancialYear':
          return { from: fyStart as number, to: fyEnd as number };
        case 'thisMonth':
          return computeThisMonthFY();
        case 'thisQuarter':
          return computeThisQuarterFY();
        case 'thisHalf':
          return computeThisHalfFY();
      }
    }

    // fallback (no FY or FY disabled): calendar-based
    const now = new Date();
    switch (key) {
      case 'today':
        return dayRangeForTs(Math.floor(now.getTime() / 1000));
      case 'thisFinancialYear': {
        // Generic “this year” (Jan 1..Dec 31) if FY isn’t available
        const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        const end   = new Date(now.getFullYear(), 12, 0, 23, 59, 59, 999);
        return { from: toUnix(start), to: toUnix(end) };
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        return { from: toUnix(start), to: toUnix(end) };
      }
      case 'thisQuarter': {
        const qStart = Math.floor(now.getMonth() / 3) * 3;
        const start = new Date(now.getFullYear(), qStart, 1, 0, 0, 0, 0);
        const end   = new Date(now.getFullYear(), qStart + 3, 0, 23, 59, 59, 999);
        return { from: toUnix(start), to: toUnix(end) };
      }
      case 'thisHalf': {
        const hStart = Math.floor(now.getMonth() / 6) * 6;
        const start = new Date(now.getFullYear(), hStart, 1, 0, 0, 0, 0);
        const end   = new Date(now.getFullYear(), hStart + 6, 0, 23, 59, 59, 999);
        return { from: toUnix(start), to: toUnix(end) };
      }
    }
  }, [
    useFYFromRedux, fyReady, fyStart, fyEnd,
    clampToFY, computeThisMonthFY, computeThisQuarterFY, computeThisHalfFY
  ]);

  const [value, setValue] = useState<Range>({ from: null, to: null });
  const [lastError, setLastError] = useState<string | null>(null);

  // initialize from defaultKey (and recompute if FY settings change)
  useEffect(() => {
    if (!defaultKey) return;
    const r = computeByKey(defaultKey);
    if (r) {
      setValue(normalizeRange(r));
      setLastError(null);
    } else {
      // e.g., 'today' while outside FY
      setLastError(`Preset "${defaultKey}" is not valid in the current financial year window.`);
    }
  }, [defaultKey, computeByKey]);

  // onChange to plug into <SimplePeriodDropdown ... />
  const onChange = useCallback((r: Range) => {
    const nr = normalizeRange(r);
    if (useFYFromRedux && fyReady && nr.from != null && nr.to != null) {
      // keep it strictly within FY
      if (nr.to < (fyStart as number) || nr.from > (fyEnd as number)) {
        setLastError('Selected range is outside the current financial year.');
        return;
      }
      const clamped: Range = {
        from: Math.max(nr.from, fyStart as number),
        to:   Math.min(nr.to,   fyEnd   as number),
      };
      setValue(clamped);
      handleChange?.(clamped);
      setLastError(null);
    } else {
      setValue(nr);
      setLastError(null);
    }
  }, [useFYFromRedux, fyReady, fyStart, fyEnd,handleChange]);

  // programmatic setter: accepts a key OR a range
  const changePeriodDropdown = useCallback((arg: PeriodKey | Range) => {
    if (typeof arg === 'string') {
      const r = computeByKey(arg);
      if (!r) {
        setLastError(`Preset "${arg}" is not valid in the current financial year window.`);
        return;
      }
      setValue(normalizeRange(r));
      setLastError(null);
    } else {
      onChange(arg);
    }
  }, [computeByKey, onChange]);

  // expose whether Today should be shown (inside FY only)
  const showTodayOption = useMemo(() => {
    if (!useFYFromRedux || !fyReady) return true;
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= (fyStart as number) && nowSec <= (fyEnd as number);
  }, [useFYFromRedux, fyReady, fyStart, fyEnd]);

  return {
    value,                 // {from,to}   -> pass as <SimplePeriodDropdown value={value} />
    onChange,              // (range)     -> pass as <SimplePeriodDropdown onChange={onChange} />
    changePeriodDropdown,  // (key | {from,to}) -> programmatic changes
    lastError,             // string | null (optional to surface errors to the UI)
    showTodayOption,       // boolean you can wire into the component's includeToday prop
  };
}
